import React from 'react'
import { CloseCircle, SearchNormal } from 'iconsax-react'
import { IconButton, useTheme } from '@mui/material'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import { separateFields } from '../CustomFieldsList/helpers'
import { ObjectType } from '../types'
import Switch from '@SonarCX/components/atoms/SwitchV2'
import CustomField from '../CustomField'
import CustomFieldInput from '@SonarCX/components/molecules/CustomFieldInput'

import useStyles from './styles'

interface CustomFieldOptionConditionProps {
  condition: APICustomFieldConfigOptionCondition
  index: number
  id?: string
  onRemoveCondition?: (val: string, index: number) => void
  onUpdateCondition?: (
    val: string,
    index: number,
    condition: APICustomFieldConfigOptionCondition
  ) => void
  type?: APICustomField['object_type']
  editing?: string
}

const CustomFieldOptionCondition: React.FC<CustomFieldOptionConditionProps> = ({
  condition,
  index,
  id,
  onRemoveCondition,
  onUpdateCondition,
  type,
  editing
}) => {
  const { customFields } = useAppSelector(selectCustomFields)
  const { external, task, user } = separateFields(customFields)
  const [search, setSearch] = React.useState('')
  const [fields, setFields] = React.useState([])
  const [shouldAddRow, setShouldAddRow] = React.useState(false)
  const [selectedField, setSelectedField] =
    React.useState<APICustomField | null>(null)

  const availableData = React.useMemo(() => {
    const trimmedSearch = search.trim().toLocaleLowerCase()
    if (trimmedSearch.length === 0) {
      return []
    }
    return fields
      .filter(
        f =>
          f.field_type !== 'table' &&
          f.field_type !== 'flow' &&
          f.id !== editing
      )
      .filter(d => {
        const searchableData = [d.title ?? '', d.description ?? '']
        const searchMatches = searchableData.some(val =>
          val.toLocaleLowerCase().includes(trimmedSearch)
        )
        return searchMatches
      })
  }, [fields, search, editing])

  const handleOnChange = React.useCallback(
    (property, value) => {
      const updated = { ...condition, [property]: value }
      onUpdateCondition(id, index, updated)
    },
    [condition, id, index, onUpdateCondition]
  )

  const handleOnRemoveMatch = React.useCallback(
    index => {
      const toUpdate = [...condition.match]
      toUpdate.splice(index, 1)
      handleOnChange('match', [...toUpdate])
    },
    [condition.match, handleOnChange]
  )

  const handleOnRemoveCondition = React.useCallback(() => {
    if (onRemoveCondition) onRemoveCondition(id, index)
  }, [id, index, onRemoveCondition])

  // Handler for CustomFieldInput component
  const handleValueChange = React.useCallback(
    (e, field, matchIndex) => {
      const toUpdate = [...condition.match]
      const { field_type } = field
      const val = field_type === 'boolean' ? e.target.checked : e.target.value

      // Store boolean as string representation
      if (field_type === 'boolean') {
        toUpdate[matchIndex] = `${val}`
        handleOnChange('match', [...toUpdate])
        return
      }

      // Store pre-formatted text
      if (
        [
          'text',
          'multiline',
          'dropdown',
          'date-only',
          'date-time',
          'month-year',
          'user',
          'phone'
        ].includes(field_type)
      ) {
        toUpdate[matchIndex] = val
        handleOnChange('match', [...toUpdate])
        return
      }

      // Logic to add or remove selections
      if (field_type === 'multiselect') {
        // Split selections, remove empty strings
        let prev = toUpdate[matchIndex].split('|').filter(Boolean)

        let newVal = ''
        if (prev.includes(val)) {
          // Remove selected value case
          newVal = prev.filter(x => x !== val).join('|')
        } else {
          // Add selection case
          newVal = [...prev, val].join('|')
        }
        toUpdate[matchIndex] = newVal
        handleOnChange('match', [...toUpdate])
        return
      }

      console.warn(`Unknown field type ${field_type}`)
    },
    [condition.match, handleOnChange]
  )

  const handleOnBlur = React.useCallback(() => {
    setShouldAddRow(true)
  }, [])

  React.useEffect(() => {
    if (condition.field_id) {
      const field = customFields.find(field => field.id === condition.field_id)
      setSelectedField(field)
    }
  }, [condition.field_id, customFields])

  React.useEffect(() => {
    if (type === ObjectType.User) {
      setFields(user)
    } else if (type === ObjectType.ExternalUser) {
      setFields(external)
    } else {
      setFields(task)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, type])

  React.useEffect(() => {
    if (!shouldAddRow) return

    setShouldAddRow(false)
    const toUpdate = [...condition.match]
    if (toUpdate[toUpdate.length - 1] === '') return

    toUpdate.push('')
    handleOnChange('match', [...toUpdate])
  }, [condition.match, handleOnChange, shouldAddRow])

  const classes = useStyles()
  const theme = useTheme()

  return (
    <div className={classes.root}>
      {selectedField ? (
        <span className={classes.field}>
          <div className={classes.titleRow}>
            <span>{selectedField.title}</span>
            <IconButton size='small' onClick={handleOnRemoveCondition}>
              <CloseCircle size={theme.size.sm} />
            </IconButton>
          </div>
          <div className={classes.row}>
            <Switch
              checked={condition.negate ?? false}
              onChange={newVal => {
                handleOnChange('negate', newVal)
              }}
              disabled={false}
            />
            <span>{condition.negate ? 'Is not' : 'Is'}</span>
          </div>
          <div>
            <div>
              {condition.match.map((match, i) => (
                <span key={`match-${i}`} className={classes.row}>
                  <CustomFieldInput
                    field={selectedField}
                    value={match}
                    handleChange={(e, field) => handleValueChange(e, field, i)}
                    handleBlur={handleOnBlur}
                    canEdit
                    inputOnly
                    meta={[]}
                  />
                  {match !== '' && (
                    <IconButton
                      onClick={() => handleOnRemoveMatch(i)}
                      size='small'
                    >
                      <CloseCircle size={theme.size.sm} />
                    </IconButton>
                  )}
                </span>
              ))}
            </div>
          </div>
        </span>
      ) : (
        <div>
          <div className={classes.searchRoot}>
            <span className={classes.searchInputContainer}>
              <SearchNormal
                className={classes.searchInputIcon}
                size={theme.size.sm}
              />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={classes.searchInput}
                placeholder='Search field'
              />
            </span>
            <IconButton size='small' onClick={handleOnRemoveCondition}>
              <CloseCircle size={theme.size.sm} />
            </IconButton>
          </div>
          {availableData.length > 0 ? (
            <div>
              {availableData.map(field => (
                <span
                  key={field.id}
                  onClick={() => {
                    setSelectedField(field)
                    handleOnChange('field_id', field.id)
                  }}
                >
                  <CustomField field={field} hideActions />
                </span>
              ))}
            </div>
          ) : (
            search.length > 0 && <span>No matching fields</span>
          )}
        </div>
      )}
    </div>
  )
}
export default CustomFieldOptionCondition
